import { useEffect, useRef, type MutableRefObject } from 'react';
import { useAtomValue } from 'jotai';
import { bridgeEventStreamAtom } from '../stores/sdk/atoms';
import { useShogunchop } from '../providers/ShogunChopProvider';

/** Trailing debounce for the cloud push:
 *  3 s — deliberately WIDER than the 1 s local-watcher debounce — so a
 *  frame-per-op storm from a busy co-authoring turn collapses into one
 *  refresh. */
const DEBOUNCE_MS = 3000;

const SELF_ECHO_WINDOW_MS = 3000;

/**
 * Cloud-workspace file-change push trigger:
 * after a mutating file op in the workspace's CLOUD pod, the Go
 * cloud-filesystem worker fans a `workspace.filesChanged` JSON-RPC frame out
 * over the bridge.
 *
 * Two mutually-exclusive arms (same split as `useWebBridgeClient`):
 *  - WEB (no daemon): register on the shared cloud {@link bridgeEventStreamAtom}
 *    published by `useWebBridgeClient` — one tab connection carries platform
 *    calls, session events, indexing, AND this fanout.
 *  - DESKTOP: the daemon owns the cloud bridge and relays the frame to its
 *    loopback `/events` stream as `{event: 'workspace.filesChanged', data:
 *    {workspace_id}}`. The renderer must NOT open a second cloud BridgeEventStream.
 *
 * NOTE the DOT event name: bridge-relayed frames keep their JSON-RPC method
 * verbatim; the local watcher's sibling trigger uses the COLON form
 * `workspace:filesChanged` (see `useWorkspaceFilesChanged` — 1 s debounce, no
 * self-echo concern because local saves don't fan back). The two forms are
 * mode-disjoint: only cloud pods produce the dot form, only locally-watched
 * workspaces produce the colon form.
 *
 * On fire (trailing edge) the hook checks `lastOwnSaveAtRef` — stamped by the
 * host on every successful write — and SKIPS the refresh inside the own-save
 * window (self-echo).
 */
export function useCloudFilesChanged({
  workspaceId,
  enabled,
  lastOwnSaveAtRef,
  onFilesChanged,
}: {
  workspaceId: string;
  enabled: boolean;
  lastOwnSaveAtRef: MutableRefObject<number>;
  onFilesChanged: () => void;
}): void {
  const stream = useAtomValue(bridgeEventStreamAtom);
  const shogunchop = useShogunchop();
  const onFilesChangedRef = useRef(onFilesChanged);
  onFilesChangedRef.current = onFilesChanged;

  const scheduleRefresh = () => {
    if (Date.now() - lastOwnSaveAtRef.current < SELF_ECHO_WINDOW_MS) return;
    onFilesChangedRef.current();
  };

  useEffect(() => {
    if (!enabled) return;
    if (!stream) return; // desktop / before identify — desktop arm below.
    if (shogunchop.resource) return; // daemon present → desktop arm owns relay.

    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = stream.onWorkspaceFilesChanged((change) => {
      if (change.workspaceId !== workspaceId) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        scheduleRefresh();
      }, DEBOUNCE_MS);
    });

    return () => {
      off();
      if (timer !== null) clearTimeout(timer);
    };
  }, [enabled, stream, shogunchop.resource, workspaceId, lastOwnSaveAtRef]);

  useEffect(() => {
    if (!enabled) return;
    const sc = shogunchop.resource;
    if (!sc) return; // web — handled above.

    const relay = sc.sharedEvents();

    let timer: ReturnType<typeof setTimeout> | null = null;
    relay.on((event) => {
      if (event.event !== 'workspace.filesChanged') return; // DOT form only
      if (event.data.workspace_id !== workspaceId) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        scheduleRefresh();
      }, DEBOUNCE_MS);
    });

    return () => {
      relay.release();
      if (timer !== null) clearTimeout(timer);
    };
  }, [enabled, shogunchop.resource, workspaceId, lastOwnSaveAtRef]);
}
